import React, { useCallback, useEffect, useState } from "react";
import { CardDetail, ManageApplicationCardControlled } from "./card-controlled";
import { ApplicationManageData, JobDetailsModel, NheDetails, SdaInfo } from "../../utility/application-data";
import { getSupportedLanguage } from "../../utility/locale-helper";
import useArb from "../../hooks/use-arb";
import { getProcessedJCAddress, isVirtualJob } from "../../utility/jc-response-helper";
import { NHEAppointmentStatusEnum } from "../appointment-card/card-controlled";
import { isSalesforceApplication } from "../../helpers/url-params";
import { isTentativeStartDateByApplicationState, resolveSdaResponse } from "../../helpers/utils";

interface ManageApplicationCardUncontrolledProps {
  salesforceAccessor: () => Promise<JobDetailsModel | undefined>;
  jcAccessor: () => Promise<JobDetailsModel | undefined>;
  sdaAccessor: () => Promise<SdaInfo | undefined>;
  applicationManageAccessor: () => Promise<ApplicationManageData | undefined>;
  nheAccessor: () => Promise<NheDetails | undefined>;
  trackScheduleChangeClick: () => void;
  trackWithdrawApplicationClick: () => void;
  trackWithdrawApplicationConfirmButtonClick: (withdrawReason: string) => void;
  cssURL: string;
  dashboardURL: string;
  applicationId: string;
  collapsed: boolean;
  appointmentSelfServiceURL: string;
}

export const ManageApplicationCardUncontrolled = ({
  applicationId,
  salesforceAccessor,
  jcAccessor,
  sdaAccessor,
  nheAccessor,
  applicationManageAccessor,
  trackScheduleChangeClick,
  trackWithdrawApplicationClick,
  trackWithdrawApplicationConfirmButtonClick,
  cssURL,
  dashboardURL,
  collapsed,
  appointmentSelfServiceURL,
}: ManageApplicationCardUncontrolledProps) => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<boolean>(false);
  const [scheduleDetails, setScheduleDetails] = useState<JobDetailsModel | undefined>();
  const [eligibleForCSS, setEligibleForCSS] = useState<boolean>(false);
  const [eligibleForWithdraw, setEligibleForWithdraw] = useState<boolean>(true);
  const [startDateDetails, setStartDateDetails] = useState<SdaInfo | undefined>();
  const [scheduleIdFound, setScheduleIdFound] = useState<boolean>(true);
  const [isTentativeStart, setIsTentativeStart] = useState(false);
  const [eligibleForAppointmentRescheduling, setEligibleForAppointmentRescheduling] = useState<boolean>(false);
  const bundle = useArb("HVHChecklistPantherResource");

  const nheIncompleteApplicationStates = ["EVALUATION_PENDING", "APPT_1_PENDING"];

  const requestJCAndSDA = useCallback(async () => {
    setLoading(true);

    try {
      if (!isSalesforceApplication(applicationId)) {
        const scheduleDetails = await jcAccessor();
        const startDateDetails = await resolveSdaResponse(applicationId, scheduleDetails, sdaAccessor);
        const applicationManageDetails = await applicationManageAccessor();
        const nheDetails = await nheAccessor();

        if (scheduleDetails?.error) {
          setError(true);
        } else {
          if (scheduleDetails?.isScheduleReleased) {
            setScheduleIdFound(false);
            setError(true);
          } else {
            setScheduleDetails(scheduleDetails);
            setError(false);
          }
        }

        if (startDateDetails?.errorCode) {
          setError(true);
        } else {
          setStartDateDetails(startDateDetails);
          setError(false);
        }

        if (
          nheDetails?.status === NHEAppointmentStatusEnum.RESERVED &&
          applicationManageDetails?.applicationState &&
          nheIncompleteApplicationStates.includes(applicationManageDetails.applicationState) &&
          !nheDetails?.isStartTimeInPast
        ) {
          setEligibleForAppointmentRescheduling(true);
        }

        if (applicationManageDetails?.enableCSS) {
          setEligibleForCSS(true);
        }
        if (applicationManageDetails?.hideWithdrawApplication) {
          setEligibleForWithdraw(false);
        }
      } else {
        const scheduleDetails = await salesforceAccessor();
        if (scheduleDetails?.error) {
          setError(true);
        } else {
          setScheduleDetails(scheduleDetails);
          setError(false);
        }
        //Customer Service job is the only job in SF. It doesn't require CSS, Rescheduling or Withdraw.
        //revisit the logic if new SF jobs (non-dsp) are added for ASH
        setEligibleForCSS(false);
        setEligibleForWithdraw(false);
        setEligibleForAppointmentRescheduling(false);
      }
    } catch (e) {
      setError(true);
    }
    setLoading(false);
  }, []);

  useEffect(() => {
    requestJCAndSDA();
  }, []);

  useEffect(() => {
    isTentativeStartDateByApplicationState(applicationId, startDateDetails).then((state) => {
      setIsTentativeStart(state);
    });
  }, [applicationId, startDateDetails]);

  const address = scheduleDetails && getProcessedJCAddress(scheduleDetails);

  const details: CardDetail = {
    jobTitle: scheduleDetails?.externalJobTitle as string,
    hours: scheduleDetails?.scheduleText as string,
    startDate: scheduleDetails?.firstDayOnSiteL10N as string,
    startTime: scheduleDetails?.startTimeL10N as string,
    description: scheduleDetails?.briefJobDescription as string,
    payRate: scheduleDetails?.totalPayRateL10N as string,
    address: address as string,
    image: scheduleDetails?.image as string,
    isTentativeStartDate: isTentativeStart,
    signOnBonus: scheduleDetails?.signOnBonusL10N as string,
    scheduleBannerText: scheduleDetails?.scheduleBannerText as string,
    hoursPerWeek: scheduleDetails?.hoursPerWeekL10N as string,
    employmentType: scheduleDetails?.employmentType as string,
    employmentTypeL10N: scheduleDetails?.employmentTypeL10N as string,
    supportedLanguage: isSalesforceApplication(applicationId)
      ? scheduleDetails?.requiredLanguageFromSalesForce
      : (getSupportedLanguage(scheduleDetails?.requiredLanguage, bundle) as string),
    parsedTrainingDate: scheduleDetails?.parsedTrainingDate as string,
    monthlyBasePayL10N: scheduleDetails?.monthlyBasePayL10N as string,
    currencyCode: scheduleDetails?.currencyCode as string,
    isVirtual: isVirtualJob(scheduleDetails) as boolean,
  };

  return (
    <ManageApplicationCardControlled
      applicationId={applicationId}
      details={details}
      onRetry={requestJCAndSDA}
      loading={loading}
      error={error}
      eligibleForCSS={eligibleForCSS}
      eligibleForWithdraw={eligibleForWithdraw}
      scheduleIdFound={scheduleIdFound}
      dashboardURL={dashboardURL}
      cssURL={cssURL}
      onChangeSchedule={trackScheduleChangeClick}
      onWithdrawApplication={trackWithdrawApplicationClick}
      onWithdrawApplicationConfirm={trackWithdrawApplicationConfirmButtonClick}
      collapsed={collapsed}
      eligibleForAppointmentRescheduling={eligibleForAppointmentRescheduling}
      appointmentSelfServiceURL={appointmentSelfServiceURL}
    />
  );
};
